import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ExternalLink } from 'lucide-react';
import { MarketingLead } from '@/lib/api/marketing-report-service';

interface Props { leads: MarketingLead[]; }

/** Twenty's saved "People" view holding inbound website leads — the whole list. */
const CRM_PEOPLE_VIEW =
  'https://crm.pagemind.ai/objects/people?viewId=be301ec5-83c7-4cf7-8eb3-c4744d95cfd9';

/**
 * Deep link to one person in Twenty.
 *
 * Route shape confirmed from the deployed CRM bundle, which registers
 * `/object/:objectNameSingular/:objectRecordId`; `person` is the object's
 * nameSingular per Twenty's metadata API.
 */
const crmPersonUrl = (id: string) => `https://crm.pagemind.ai/object/person/${id}`;

export function MarketingLeadsTable({ leads }: Props) {
  if (leads.length === 0) {
    return (
      <Card>
        <CardHeader><CardTitle>Website Leads</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No website-cta leads in this range.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle>Website Leads <span className="text-base font-normal text-muted-foreground">({leads.length})</span></CardTitle>
        {/* The list as a whole — the per-row links go to individual people. */}
        <a
          href={CRM_PEOPLE_VIEW}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm font-normal text-primary hover:underline"
        >
          Open in CRM
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
        </a>
      </CardHeader>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="px-4 py-2 text-left font-medium text-muted-foreground">Name</th>
              <th className="px-4 py-2 text-left font-medium text-muted-foreground">Company</th>
              <th className="px-4 py-2 text-left font-medium text-muted-foreground">Message</th>
              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Date ↓</th>
              <th className="px-4 py-2 text-right font-medium text-muted-foreground">CRM</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((lead, i) => (
              <tr key={i} className="border-b last:border-0 hover:bg-muted/30 align-top">
                <td className="px-4 py-2 font-medium">{lead.name || '—'}</td>
                <td className="px-4 py-2">{lead.company || '—'}</td>
                <td className="px-4 py-2 max-w-xs text-muted-foreground">
                  <span title={lead.message}>{lead.message ? lead.message.slice(0, 100) + (lead.message.length > 100 ? '…' : '') : '—'}</span>
                </td>
                <td className="px-4 py-2 text-right tabular-nums text-muted-foreground whitespace-nowrap">
                  {lead.createdAt
                    ? new Date(lead.createdAt).toLocaleString(undefined, {
                        year: 'numeric', month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })
                    : '—'}
                </td>
                {/* No link when the note has no linked person: sending every such row
                    to the same list view would look like navigation to that lead and
                    silently be something else. */}
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {lead.crmPersonId ? (
                    <a
                      href={crmPersonUrl(lead.crmPersonId)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                      aria-label={`Open ${lead.name || 'this lead'} in the CRM`}
                    >
                      See in CRM
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    </a>
                  ) : (
                    <span className="text-muted-foreground" title="No linked CRM person">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
